/**
 * Hero2Section — secondary hero with a "Stack & Stamp" bullet animation.
 *
 * Visual:  centred copy on top  →  wide 16:10 image with a green offset
 *          accent panel behind it. A stack of bullet "pills" on the left
 *          accumulates one-by-one with a green-check stamp animation, then
 *          fades out and restarts. Conveys "look at all the things you
 *          can do quickly on the touchline" — a fast to-do checklist.
 *
 * CMS namespace: content.hero2 = { eyebrow, title, subtitle, bgImageUrl,
 *   bullets:[{id, icon, text}], speedMs, maxStack, holdMs }
 */
import { useState, useEffect, useRef } from 'react';
import {
  BadgeCheck, Star, Flame, Zap, Trophy, Medal, Target, Timer,
  Users, ShieldCheck, Radio, ClipboardList, Heart, PlayCircle,
  Smartphone, LineChart, Check
} from 'lucide-react';

export const HERO2_ICONS = {
  'badge-check': BadgeCheck,
  'star': Star,
  'flame': Flame,
  'zap': Zap,
  'trophy': Trophy,
  'medal': Medal,
  'target': Target,
  'timer': Timer,
  'users': Users,
  'shield-check': ShieldCheck,
  'radio': Radio,
  'clipboard-list': ClipboardList,
  'heart': Heart,
  'play-circle': PlayCircle,
  'smartphone': Smartphone,
  'line-chart': LineChart,
};

const DEFAULT_BULLETS = [
  { id: 1, icon: 'badge-check', text: 'Match Stats Updated Live' },
  { id: 2, icon: 'flame', text: 'In-Form Player Tracker' },
  { id: 3, icon: 'clipboard-list', text: 'Tactics Board On Pitch' },
  { id: 4, icon: 'medal', text: 'Auto Player Highlights' },
  { id: 5, icon: 'heart', text: 'Welfare Tracking Built-In' },
];

const STEP = 88;                       // px between adjacent pills on the drum
const TRANSITION_MS = 650;             // drum rotation duration
const EASE = 'cubic-bezier(.34,.95,.38,1)';
const VISIBLE_SLOTS = 5;               // how many pill heights fit in the carousel window
const CONTAINER_H = STEP * VISIBLE_SLOTS;

function DrumPill({ bullet, dist, top, snapping = false }) {
  const absDist = Math.abs(dist);
  // Flat-facing pills (no rotateX) — distance is expressed via scale, opacity, blur.
  let scale = 0.62, opacity = 0, blur = 1.4;
  if (absDist === 0)      { scale = 1.06; opacity = 1;    blur = 0; }
  else if (absDist === 1) { scale = 0.92; opacity = 0.78; blur = 0.2; }
  else if (absDist === 2) { scale = 0.78; opacity = 0.32; blur = 0.6; }
  const isActive = absDist === 0;
  const IconComp = HERO2_ICONS[bullet.icon] || BadgeCheck;
  // During a wheel snap-back we re-key pill positions wholesale; transitions
  // would animate the dramatic dist changes and cause a visible flicker.
  const transition = snapping
    ? 'none'
    : `transform ${TRANSITION_MS}ms ${EASE}, opacity ${TRANSITION_MS}ms ${EASE}, filter ${TRANSITION_MS}ms ${EASE}, background-color 240ms ease, box-shadow 240ms ease`;
  return (
    <div
      className="absolute left-0 right-0 flex items-center gap-3 px-4 py-3 rounded-2xl"
      style={{
        top: `${top}px`,
        transform: `scale(${scale})`,
        opacity,
        filter: blur ? `blur(${blur}px)` : 'none',
        transition,
        backgroundColor: isActive ? 'rgba(255,255,255,0.96)' : 'rgba(245,245,244,0.78)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        boxShadow: isActive ? '0 12px 32px rgba(0,0,0,.32)' : '0 6px 18px rgba(0,0,0,.18)',
        transformOrigin: 'center center',
      }}
      data-testid={`hero2-drum-pill-${bullet.id ?? bullet.text}`}
    >
      <IconComp className="w-5 h-5 flex-shrink-0 text-gray-900" />
      <span className="text-sm font-bold text-gray-900 flex-1 whitespace-nowrap overflow-hidden text-ellipsis">{bullet.text}</span>
      <span
        className="w-6 h-6 rounded-full bg-emerald-600 flex items-center justify-center flex-shrink-0"
        style={{
          opacity: isActive ? 1 : 0,
          transform: isActive ? 'scale(1)' : 'scale(.6)',
          boxShadow: '0 4px 10px rgba(22,163,74,.45)',
          transition: snapping ? 'none' : 'opacity 280ms ease 280ms, transform 280ms cubic-bezier(.5,1.6,.4,1) 280ms',
        }}
      >
        <Check className="w-3.5 h-3.5 text-white" strokeWidth={3.5} />
      </span>
    </div>
  );
}

export function Hero2Section({ content, theme, t }) {
  const h = content?.hero2 || {};
  const bullets = (Array.isArray(h.bullets) && h.bullets.length > 0) ? h.bullets : DEFAULT_BULLETS;
  const eyebrow = h.eyebrow || 'BUILT FOR MATCH DAY';
  const title = h.title || 'From the touchline to the back of the net.';
  const subtitle = h.subtitle || 'Log every goal, card and sub the second it happens — and watch your stats, charts and player insights update in real time.';
  // Visibility toggles — default to true so existing CMS records are unaffected.
  const showEyebrow  = h.show_eyebrow  !== false;
  const showTitle    = h.show_title    !== false;
  const showSubtitle = h.show_subtitle !== false;
  const anyTopVisible = (showEyebrow && eyebrow) || (showTitle && title) || (showSubtitle && subtitle);
  const bgImageUrl = h.bgImageUrl || '';
  const speedMs = Math.max(400, Number(h.speedMs ?? 800));
  // maxStack is retained for CMS-content backwards compatibility but is no
  // longer consumed by the drum animation.
  // eslint-disable-next-line no-unused-vars
  const maxStack = Math.max(2, Math.min(8, Number(h.maxStack ?? 5)));
  const holdMs = Math.max(500, Number(h.holdMs ?? 1500));

  const n = bullets.length;
  // Drum cycle state. We render 3 copies of the bullets back-to-back so the
  // wheel can scroll continuously; when wheelPos passes the second copy we
  // snap back invisibly by n positions.
  const [wheelPos, setWheelPos] = useState(n);
  const [noTransition, setNoTransition] = useState(false);
  // Only rotate while the section is on screen. Saves cycles when scrolled
  // away and ensures the first rotation lines up with the user seeing the
  // section for the first time.
  const [isOnScreen, setIsOnScreen] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(
      ([entry]) => setIsOnScreen(entry.isIntersecting),
      { threshold: 0.2 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Period between rotations — derives from existing CMS knobs so admins
  // don't lose their tuning. Floor at 800ms to keep transitions readable.
  const cycleMs = Math.max(800, speedMs + holdMs);

  useEffect(() => {
    if (n === 0 || !isOnScreen) return undefined;
    const interval = setInterval(() => {
      setWheelPos(p => p + 1);
    }, cycleMs);
    return () => clearInterval(interval);
  }, [n, cycleMs, isOnScreen]);

  // Snap-back when we've rolled past the second copy of bullets.
  useEffect(() => {
    if (n === 0 || wheelPos < 2 * n) return undefined;
    const t = setTimeout(() => {
      setNoTransition(true);
      setWheelPos(p => p - n);
      requestAnimationFrame(() => {
        // Re-enable transitions on the next frame so the next rotation animates.
        requestAnimationFrame(() => setNoTransition(false));
      });
    }, TRANSITION_MS + 30);
    return () => clearTimeout(t);
  }, [wheelPos, n]);

  const trackTy = (CONTAINER_H / 2) - (wheelPos * STEP) - (STEP / 2);

  const isDark = theme === 'dark';
  const titleCls = isDark ? 'text-white' : 'text-gray-900';
  const subtitleCls = isDark ? 'text-gray-400' : 'text-gray-600';
  const cardFallbackBg = isDark ? '#0b1b12' : '#1f2937';

  return (
    <section
      ref={sectionRef}
      data-section="hero2"
      data-testid="hero2-section"
      className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8"
    >
      <div className="max-w-6xl mx-auto">
        {/* Top: eyebrow + title + subtitle */}
        {anyTopVisible && (
          <div className="max-w-3xl mx-auto text-center mb-10 sm:mb-14">
            {showEyebrow && eyebrow && (
              <span
                className="inline-block px-3 py-1 rounded-full bg-[#007a33] text-white text-xs font-extrabold tracking-[1.4px] uppercase mb-4"
                data-testid="hero2-eyebrow"
              >
                {eyebrow}
              </span>
            )}
            {showTitle && title && (
              <h2 className={`text-3xl sm:text-4xl font-bold leading-tight ${titleCls}`} data-testid="hero2-title">
                {title}
              </h2>
            )}
            {showSubtitle && subtitle && (
              <p className={`mt-4 text-base sm:text-lg leading-relaxed ${subtitleCls}`} data-testid="hero2-subtitle">
                {subtitle}
              </p>
            )}
          </div>
        )}

        {/* Bottom: wide image + offset accent + stack carousel.
            Mobile gets a min-height tall enough to fit the drum and skips
            the 16:10 aspect ratio (otherwise the intrinsic size widens past
            the viewport). Desktop keeps 16:10 for the cinematic feel. */}
        <div
          className="relative mx-auto min-h-[500px] sm:min-h-0 sm:aspect-[16/10]"
          style={{ maxWidth: '1100px' }}
          data-testid="hero2-visual"
        >
          {/* Green offset accent panel — extends only on the left and right
              of the image. Top and bottom use matching positive insets so the
              image fully covers the green vertically. */}
          <div className="absolute -left-2 -right-2 top-2 bottom-2 sm:-left-4 sm:-right-4 sm:top-4 sm:bottom-4 rounded-3xl bg-[#007a33] -z-0" />
          {/* Image card — anchored right on mobile so the two coaches stay
              visible behind the centred drum; centered on desktop. Uses an
              <img> tag so object-position responds reliably to breakpoints. */}
          <div
            className="absolute inset-0 rounded-3xl overflow-hidden"
            style={{
              backgroundColor: cardFallbackBg,
              boxShadow: '0 30px 60px -20px rgba(0,0,0,.4)',
            }}
            data-testid="hero2-bg-image"
          >
            {bgImageUrl && (
              <img
                src={bgImageUrl}
                alt=""
                aria-hidden="true"
                className="w-full h-full object-cover object-right sm:object-center"
                draggable="false"
              />
            )}
          </div>
          {/* Drum carousel overlay (LEFT side on desktop, centred on mobile) */}
          <div
            className="absolute left-1/2 sm:left-6 top-1/2 -translate-x-1/2 sm:translate-x-0 -translate-y-1/2 w-[88%] sm:w-[44%] max-w-[380px] px-[22px] overflow-hidden pointer-events-none"
            style={{
              height: `${CONTAINER_H}px`,
              WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, #000 18%, #000 82%, transparent 100%)',
              maskImage: 'linear-gradient(to bottom, transparent 0%, #000 18%, #000 82%, transparent 100%)',
            }}
            data-testid="hero2-drum-wrap"
          >
            <div
              className="relative w-full h-full"
              style={{
                transform: `translateY(${trackTy}px)`,
                transition: noTransition ? 'none' : `transform ${TRANSITION_MS}ms ${EASE}`,
                willChange: 'transform',
              }}
              data-testid="hero2-drum-track"
            >
              {n > 0 && Array.from({ length: 3 }).flatMap((_, copy) =>
                bullets.map((b, i) => {
                  const absI = copy * n + i;
                  const dist = absI - wheelPos;
                  return (
                    <DrumPill
                      key={`${copy}-${b.id ?? i}`}
                      bullet={b}
                      dist={dist}
                      top={absI * STEP}
                      snapping={noTransition}
                    />
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
